import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Card, Empty, Failure, Field, Loading, Notice } from '../components/ui.js';
import {
  CHALK_ICONS,
  ChalkArt,
  DEFAULT_SECONDS,
  SLIDE_KIND_LABELS,
  SlideView,
  type BoardSlide,
} from '../components/BoardSlides.js';
import { SHEET_MAX_COLS, SHEET_MAX_ROWS, readSheet } from '../lib/sheet.js';

interface School {
  id: string;
  name: string;
}

const MAX_SLIDES = 20;
const MAX_IMAGE_BYTES = 300 * 1024;

/** What each kind of slide is for, in the words of whoever runs the school. */
const KIND_HINTS: Record<BoardSlide['kind'], { icon: string; hint: string }> = {
  chalk: { icon: '✍️', hint: 'عنوان ونص على السبورة' },
  image: { icon: '🖼️', hint: 'صورة من الحاسبة' },
  video: { icon: '🎬', hint: 'فيديو برابط' },
  table: { icon: '📊', hint: 'جدول من ملف Excel' },
};

function newSlide(kind: BoardSlide['kind']): BoardSlide {
  const base = { id: `${kind}-${Math.random().toString(36).slice(2, 9)}`, kind, visible: true };
  switch (kind) {
    case 'image':
      return { ...base, kind: 'image', title: '' };
    case 'video':
      return { ...base, kind: 'video', title: '', video: '' };
    case 'table':
      return { ...base, kind: 'table', title: 'نتائج الامتحان', cols: ['الاسم', 'الدرجة'], grid: [] };
    default:
      return {
        ...base,
        kind: 'chalk',
        icon: 'cap',
        title: 'مبارك لطلبتنا المتفوقين',
        body: 'بالعلم والاجتهاد نصنع المستقبل',
      };
  }
}

/**
 * Where the school writes its board. The board is a slide show, so the page is
 * a list of slides and one big preview of the slide being edited — no canvas to
 * arrange, nothing to drag. Nothing reaches the screens until «حفظ وعرض».
 */
export function BoardEditorPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [schoolId, setSchoolId] = useState('');
  const [slides, setSlides] = useState<BoardSlide[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState<{ tone: 'info' | 'warn' | 'danger'; text: string } | null>(null);

  const schools = useQuery({
    queryKey: ['schools', 'board-editor'],
    queryFn: () => get<{ items: School[] }>('/schools?page_size=200'),
  });
  const activeSchool = schoolId || schools.data?.items[0]?.id || '';

  const board = useQuery({
    queryKey: ['board', activeSchool],
    queryFn: () => get<{ slides: BoardSlide[] }>(`/schools/${activeSchool}/board`),
    enabled: Boolean(activeSchool),
    // A refetch mid-edit would throw away the work: the board is read once per
    // school and then owned by this page until it is saved.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!board.data || loadedFor.current === activeSchool) return;
    loadedFor.current = activeSchool;
    setSlides(board.data.slides);
    setSelected(board.data.slides[0]?.id ?? null);
    setDirty(false);
  }, [board.data, activeSchool]);

  const save = useMutation({
    mutationFn: () => put(`/schools/${activeSchool}/board`, { slides }),
    onSuccess: () => {
      setDirty(false);
      setNote({ tone: 'info', text: 'حُفظت اللوحة وتظهر على الشاشات خلال ثوانٍ.' });
      queryClient.setQueryData(['board', activeSchool], { slides });
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const current = useMemo(() => slides.find((s) => s.id === selected) ?? null, [slides, selected]);

  const update = (id: string, patch: Partial<BoardSlide>): void => {
    setDirty(true);
    setSlides((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const add = (kind: BoardSlide['kind']): void => {
    if (slides.length >= MAX_SLIDES) {
      setNote({ tone: 'warn', text: `أقصى عدد للشرائح ${MAX_SLIDES}.` });
      return;
    }
    const slide = newSlide(kind);
    setSlides((list) => [...list, slide]);
    setSelected(slide.id);
    setDirty(true);
  };

  const remove = (id: string): void => {
    setSlides((list) => {
      const next = list.filter((s) => s.id !== id);
      setSelected(next[0]?.id ?? null);
      return next;
    });
    setDirty(true);
  };

  const duplicate = (slide: BoardSlide): void => {
    const copy = { ...slide, id: `${slide.kind}-${Math.random().toString(36).slice(2, 9)}` };
    setSlides((list) => [...list, copy]);
    setSelected(copy.id);
    setDirty(true);
  };

  /** Order is the order they are shown in, so moving one is moving its turn. */
  const move = (id: string, by: -1 | 1): void => {
    setSlides((list) => {
      const at = list.findIndex((s) => s.id === id);
      const to = at + by;
      if (at < 0 || to < 0 || to >= list.length) return list;
      const next = [...list];
      const [slide] = next.splice(at, 1);
      next.splice(to, 0, slide!);
      return next;
    });
    setDirty(true);
  };

  const onImage = (file: File): void => {
    if (!current) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setNote({ tone: 'danger', text: 'حجم الصورة أكبر من 300 ك.ب؛ اختر صورة أصغر أو اضغطها.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update(current.id, { image: String(reader.result) });
    reader.readAsDataURL(file);
  };

  const onSheet = async (file: File): Promise<void> => {
    if (!current) return;
    try {
      const table = await readSheet(file);
      update(current.id, {
        cols: table.cols,
        grid: table.rows,
        title: current.title || file.name.replace(/\.[a-z]+$/i, ''),
      });
      setNote({
        tone: 'info',
        text: `قُرئ الملف: ${table.rows.length} سطرًا و${table.cols.length} أعمدة. الأسطر الزائدة تُعرض بالتناوب على الشاشة.`,
      });
    } catch (e) {
      setNote({ tone: 'danger', text: (e as Error).message || 'تعذّرت قراءة الملف' });
    }
  };

  if (!can('school.manage')) {
    return (
      <Card title="لوحة إعلانات المدرسة">
        <Notice tone="warn">تعديل اللوحة يتطلب صلاحية إدارة المدارس.</Notice>
      </Card>
    );
  }

  const visible = slides.filter((s) => s.visible).length;

  return (
    <>
      {note ? <Notice tone={note.tone}>{note.text}</Notice> : null}

      <Card
        title="لوحة إعلانات المدرسة"
        subtitle="شرائح تتناوب على سبورة الشاشة — أضف شريحة، اكتب فيها، ثم «حفظ وعرض»"
        actions={
          <>
            <select
              value={activeSchool}
              onChange={(e) => {
                loadedFor.current = null;
                setSchoolId(e.target.value);
              }}
              style={{ width: 'auto' }}
              aria-label="المدرسة"
            >
              {(schools.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button className="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'جارٍ الحفظ…' : dirty ? 'حفظ وعرض' : 'محفوظة'}
            </button>
          </>
        }
      >
        <div className="nb-add">
          {(Object.keys(SLIDE_KIND_LABELS) as Array<BoardSlide['kind']>).map((kind) => (
            <button key={kind} className="nb-add-btn" onClick={() => add(kind)}>
              <span className="nb-add-icon" aria-hidden="true">
                {KIND_HINTS[kind].icon}
              </span>
              <span>
                <strong>{SLIDE_KIND_LABELS[kind]}</strong>
                <small>{KIND_HINTS[kind].hint}</small>
              </span>
            </button>
          ))}
          <div className="nb-add-tools">
            <span className="nb-count">
              {visible} ظاهرة من {slides.length}
            </span>
          </div>
        </div>

        {schools.isLoading || board.isLoading ? (
          <Loading />
        ) : board.error ? (
          <Failure error={board.error} onRetry={() => void board.refetch()} />
        ) : (
          <div className="nb-editor">
            <div>
              {/* The preview is the screen's own board, at the school's scale. */}
              <div className="sd-board nb-preview">
                <div className="sd-board-top">
                  <span className="sd-board-title">لوحة الإعلانات</span>
                  {current ? (
                    <span className="sd-counter num" dir="ltr">
                      {String(slides.findIndex((s) => s.id === current.id) + 1).padStart(2, '0')} /{' '}
                      {String(slides.length).padStart(2, '0')}
                    </span>
                  ) : null}
                </div>
                <div className="sd-slide" key={current?.id ?? 'empty'}>
                  {current ? (
                    <SlideView slide={current} />
                  ) : (
                    <div className="bd-chalk">
                      <h2>لا توجد شرائح</h2>
                    </div>
                  )}
                </div>
                <div className="sd-chalks" aria-hidden="true">
                  <i />
                  <i />
                  <span className="sd-eraser" />
                </div>
              </div>

              <div className="nb-strip">
                {slides.length === 0 ? (
                  <p className="nb-help">لا توجد شرائح بعد — أضف واحدة من الأزرار أعلاه.</p>
                ) : null}
                {slides.map((s, i) => (
                  <div
                    key={s.id}
                    className={`nb-chip ${s.id === selected ? 'on' : ''} ${s.visible ? '' : 'off'}`}
                    onClick={() => setSelected(s.id)}
                  >
                    <span className="nb-chip-no num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="nb-chip-icon" aria-hidden="true">
                      {KIND_HINTS[s.kind].icon}
                    </span>
                    <span className="nb-chip-name">{s.title || SLIDE_KIND_LABELS[s.kind]}</span>
                    <span className="nb-chip-tools">
                      <button title="تقديم" onClick={(e) => (e.stopPropagation(), move(s.id, -1))} disabled={i === 0}>
                        ⟨
                      </button>
                      <button
                        title="تأخير"
                        onClick={(e) => (e.stopPropagation(), move(s.id, 1))}
                        disabled={i === slides.length - 1}
                      >
                        ⟩
                      </button>
                      <button
                        title={s.visible ? 'إخفاء' : 'إظهار'}
                        onClick={(e) => (e.stopPropagation(), update(s.id, { visible: !s.visible }))}
                      >
                        {s.visible ? '👁' : '🚫'}
                      </button>
                      <button title="حذف" className="rm" onClick={(e) => (e.stopPropagation(), remove(s.id))}>
                        🗑
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="nb-props">
              {!current ? (
                <Empty title="اختر شريحة" hint="اضغط شريحة من القائمة لتعديلها، أو أضف شريحة جديدة." />
              ) : (
                <>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <strong>
                      {KIND_HINTS[current.kind].icon} {SLIDE_KIND_LABELS[current.kind]}
                    </strong>
                    <label className="row" style={{ gap: 6, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={current.visible}
                        onChange={(e) => update(current.id, { visible: e.target.checked })}
                      />
                      ظاهرة على الشاشة
                    </label>
                  </div>

                  <Field label={current.kind === 'chalk' ? 'العنوان' : 'العنوان (اختياري)'}>
                    <input
                      value={current.title ?? ''}
                      maxLength={160}
                      onChange={(e) => update(current.id, { title: e.target.value })}
                    />
                  </Field>

                  {current.kind === 'chalk' ? (
                    <>
                      <Field label="النص تحت العنوان">
                        <textarea
                          rows={3}
                          value={current.body ?? ''}
                          maxLength={600}
                          onChange={(e) => update(current.id, { body: e.target.value })}
                        />
                      </Field>
                      <Field label="الرسم بالطباشير" hint="اختر من القائمة، والمعاينة فوق مباشرة">
                        <div className="nb-icon-pick">
                          {Object.entries(CHALK_ICONS).map(([name, icon]) => (
                            <button
                              key={name}
                              type="button"
                              title={icon.label}
                              className={(current.icon ?? 'cap') === name ? 'on' : ''}
                              onClick={() => update(current.id, { icon: name })}
                            >
                              {name === 'none' ? <span className="nb-icon-none">∅</span> : <ChalkArt name={name} />}
                              <small>{icon.label}</small>
                            </button>
                          ))}
                        </div>
                      </Field>
                    </>
                  ) : null}

                  {current.kind === 'image' ? (
                    <Field label="الصورة" hint="PNG أو JPG حتى 300 ك.ب">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) onImage(file);
                        }}
                      />
                      {current.image ? (
                        <div className="nb-table-info">
                          <span>الصورة محمّلة.</span>
                          <button className="ghost" onClick={() => update(current.id, { image: undefined })}>
                            إزالة الصورة
                          </button>
                        </div>
                      ) : null}
                    </Field>
                  ) : null}

                  {current.kind === 'video' ? (
                    <Field
                      label="رابط الفيديو"
                      hint="رابط https مباشر لملف mp4 أو webm — يعمل بصمت ويعيد نفسه"
                    >
                      <input
                        value={current.video ?? ''}
                        maxLength={600}
                        placeholder="https://example.com/clip.mp4"
                        onChange={(e) => update(current.id, { video: e.target.value.trim() })}
                      />
                    </Field>
                  ) : null}

                  {current.kind === 'table' ? (
                    <Field
                      label="جدول من Excel"
                      hint={`ملف .xlsx أو .csv — أول سطر عناوين الأعمدة (حتى ${SHEET_MAX_COLS} أعمدة و${SHEET_MAX_ROWS} سطرًا)`}
                    >
                      <input
                        type="file"
                        accept=".xlsx,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) void onSheet(file);
                        }}
                      />
                      <div className="nb-table-info">
                        {current.grid?.length ? (
                          <>
                            <span>
                              {current.grid.length} سطرًا · {(current.cols ?? []).join(' / ')}
                            </span>
                            <button className="ghost" onClick={() => update(current.id, { grid: [], cols: [] })}>
                              تفريغ الجدول
                            </button>
                          </>
                        ) : (
                          <span>لم يُحمَّل جدول بعد.</span>
                        )}
                      </div>
                      <a
                        className="nb-template"
                        download="نموذج-النتائج.csv"
                        href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                          '﻿الاسم,الدرجة\nمحمد علي,98\nزينب حسن,95\n',
                        )}`}
                      >
                        تنزيل نموذج جاهز للتعبئة
                      </a>
                    </Field>
                  ) : null}

                  <Field label="مدة العرض" hint="بالثواني قبل الانتقال إلى الشريحة التالية">
                    <input
                      type="number"
                      min={4}
                      max={600}
                      value={current.seconds ?? DEFAULT_SECONDS[current.kind]}
                      onChange={(e) =>
                        update(current.id, { seconds: Math.max(4, Math.min(600, Number(e.target.value) || 18)) })
                      }
                      style={{ width: 120 }}
                    />
                  </Field>

                  <div className="row" style={{ gap: 6, marginTop: 8 }}>
                    <button className="ghost" onClick={() => duplicate(current)}>
                      نسخ
                    </button>
                    <button className="danger" onClick={() => remove(current.id)}>
                      حذف
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
